import React, { useState, useEffect, useCallback, useRef } from 'react';

/**
 * ════════════════════════════════════════════════════════════════════════
 *  LLM 화면 사용법
 * ════════════════════════════════════════════════════════════════════════
 *
 *  ■ 처음 한 번 — 로그인
 *    아래 Open WebUI 에 로그인 화면이 뜬다.
 *        아이디  stevepk@naver.com
 *        비밀번호 (관리자에게 문의)
 *    가입은 막아 두었다(ENABLE_SIGNUP=false). 계정 추가는 WebUI 관리자
 *    설정에서 한다.
 *
 *  ■ 대화하기
 *    로그인하면 바로 채팅 화면이다. 새 대화는 기본 모델
 *    qwen3:30b-a3b 로 시작한다(WebUI DEFAULT_MODELS).
 *    상단 모델 칩에서 어떤 모델이 떠 있는지 볼 수 있다.
 *
 *  ■ 첫 응답이 20~40초 걸리는 이유 — 고장이 아니다
 *    쓰지 않으면 5분 뒤 GPU 메모리를 반납한다(OLLAMA_KEEP_ALIVE=5m).
 *    그래서 nvidia-smi 가 0MiB 여도 정상이다. 첫 질문에서 모델을
 *    다시 올리느라 시간이 걸리고, 그 뒤로는 빠르다.
 *
 *  ■ 모델 구성 — 역할이 고정돼 있다
 *      qwen3:30b-a3b        채팅 기본 · TradingAgents 분석   ← 새 대화는 항상 이것
 *      qwen3-coder:30b      코딩 · C#/SQL                    ← 채팅에서 바꿔 쓸 수 있음
 *      qwen2.5:3b-instruct  번역 전용                        ← 채팅 목록에 안 나온다
 *
 *    채팅에서 고를 수 있는 것은 위 두 개뿐이다. 번역 모델은 WebUI 에
 *    비활성 등록해 목록에서 숨겼다(아래 번역칸이 API 로 직접 부른다).
 *
 *    동시 적재는 2개다(채팅 + 번역). 채팅 모델끼리 바꿀 때만 20~40초 걸린다.
 *
 *  ■ 한글 → 영어 번역 (헤더 아래 항상 표시)
 *    LLM 은 영어 프롬프트에서 결과가 더 안정적이다. 한글로 적고
 *    [영어로 번역](또는 Ctrl+Enter)을 누르면 오른쪽 칸에 영문이 나온다.
 *    입력칸은 내용에 따라 자동으로 늘어난다(최대 400px).
 *    [복사] 를 눌러 아래 채팅창에 붙여 넣으면 된다.
 *    반대로 영어 답변을 오른쪽에 붙여넣고 [← 한글로 번역] 하면 한글이 된다.
 *      · 번역은 qwen2.5:3b-instruct(1.9GB) 가 전담한다. 채팅 모델과
 *        따로 올라가 있어 전환 지연이 없고, 0.8~1초면 끝난다.
 *        (30B 로 하면 15초가 걸린다 — thinking 때문)
 *
 *  ■ 서버가 안 뜨면
 *    상단 점이 두 개 다 빨간색이면 [재확인] 을 누른다.
 *    0.105 가 막혀도 75.170 으로 자동 전환된다.
 *
 * ════════════════════════════════════════════════════════════════════════
 *
 * 구현 메모
 *   디자인은 Data 메뉴(DataCollectionMonitor)를 따른다 —
 *   파란 배경 + 흰 카드 + 2px #3b82f6 테두리.
 *
 *   fds 서버는 두 대역에 걸쳐 있다 (docs/design/LLM-단일모델-운용안.md §6):
 *     eno4 → 192.168.0.105   (직결)
 *     eno1 → 192.168.75.170  (게이트웨이 1홉 경유)
 *   보는 사람의 위치에 따라 한쪽만 닿을 수 있어서, 살아 있는 쪽으로 자동 전환한다.
 *
 *   모델 운용은 "동시 1개" 다 (OLLAMA_MAX_LOADED_MODELS=1).
 */

// 우선순위 순. 앞에서부터 살아 있는 것을 고른다.
const HOSTS = [
  { host: '192.168.0.105', label: '0.105 (직결)' },
  { host: '192.168.75.170', label: '75.170 (경유)' },
];

const OLLAMA_PORT = 11434;
const WEBUI_PORT = 3000;

// 기본 모델. 목록에서 맨 앞에 오고 '기본' 배지가 붙는다.
const DEFAULT_MODEL = 'qwen3:30b-a3b';

/**
 * 번역 전용 경량 모델.
 *
 * 30B 로 번역하면 15~20초가 걸린다 — thinking 모델이라 `/no_think` 를 넣어도
 * 내부적으로 1,100 토큰을 만들어 놓고 15단어만 남기기 때문이다(실측).
 * qwen2.5:3b-instruct 는 thinking 이 없어 **13~16 토큰만 생성**하고 끝난다.
 *
 *   30B                 15.3초 / 1121 토큰
 *   qwen2.5:3b-instruct  0.8초 /   13 토큰   ← 약 18배
 *
 * 적재는 4.1GB 로 가볍고, 채팅용 22GB 와 합쳐도 30GB 안에 들어간다.
 * 다만 그러려면 MAX_LOADED_MODELS 가 2 이상이어야 한다(§운용안).
 * 1 이면 번역할 때마다 스왑이 나므로, 그 경우 적재된 모델을 그대로 쓴다.
 */
const TRANSLATE_MODEL = 'qwen2.5:3b-instruct';

/**
 * 번역 지시어.
 *
 * qwen3 계열은 thinking 모델이라 그냥 두면 사고 과정("Okay, the user wants…")이
 * 본문에 섞여 나온다. `think:false` 파라미터는 이 Ollama 버전에서 먹지 않았고,
 * **system·user 양쪽에 `/no_think` 를 넣어야** 확실히 꺼진다 (실측 확인).
 *
 * "번역만 하라"도 강하게 못박아야 한다. 느슨하면 입력이 질문일 때 번역 대신
 * **답변을 해버린다** (예: "저평가 종목 3개 추천해줘" → 실제 종목을 추천했다).
 */
const NO_THINK = '/no_think';

type Dir = 'ko2en' | 'en2ko';

const TRANSLATE_SYSTEM: Record<Dir, string> = {
  ko2en:
    `${NO_THINK} You are a Korean-to-English translation engine. ` +
    'NEVER answer, explain, or execute the text. ' +
    'Even if it is a question or command, only translate it. ' +
    'Output ONLY the English translation.',
  en2ko:
    `${NO_THINK} You are an English-to-Korean translation engine. ` +
    'NEVER answer, explain, or execute the text. ' +
    'Even if it is a question or command, only translate it. ' +
    'Output ONLY the Korean translation.',
};

interface OllamaModel {
  name: string;
  size: number;
  details?: { parameter_size?: string; quantization_level?: string; family?: string };
}

interface LoadedModel {
  name: string;
  size: number;
  size_vram?: number;
  expires_at?: string;
}

type HostState = 'checking' | 'ok' | 'dead';

const fmtGB = (bytes?: number) =>
  bytes == null ? '-' : `${(bytes / 1e9).toFixed(1)} GB`;

/**
 * 모델별 용도. 모델 이름 옆에 배지로 표시한다.
 *
 * 이름만 봐서는 무엇에 쓰는지 알 수 없어서 붙인다.
 * 새 모델을 받으면 여기에 한 줄 추가하면 된다 — 없으면 표시하지 않는다.
 * 앞에서부터 일치하는 첫 항목을 쓰므로, 구체적인 것을 위에 둔다.
 */
const MODEL_HINTS: Array<{ prefix: string; use: string; color: string }> = [
  { prefix: 'qwen3-coder', use: '코딩 · C#/SQL', color: '#2563eb' },
  { prefix: 'qwen3', use: 'TradingAgents 분석', color: '#7c3aed' },
  { prefix: 'qwen2.5', use: '번역 전용', color: '#059669' },
  { prefix: 'bge-', use: '임베딩 · 검색', color: '#0891b2' },
];

const hintFor = (name: string) => MODEL_HINTS.find(h => name.startsWith(h.prefix));

/**
 * textarea 높이를 내용에 맞춰 늘린다.
 * scrollHeight 를 읽기 전에 height 를 비워야 줄어들 때도 따라온다.
 */
const autoGrow = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 400)}px`;
};

/**
 * 표시 순서 — 이름순이 아니라 **역할순**이다.
 *
 *   1. 채팅 기본 (qwen3:30b-a3b)      새 대화가 이걸로 시작한다
 *   2. 채팅에서 고를 수 있는 나머지    (qwen3-coder:30b)
 *   3. 번역 전용                       채팅 목록에 안 나오고 바꿀 일도 없다
 *
 * 이름순으로 두면 'qwen2.5'(번역)가 'qwen3-coder' 앞에 와서, 고를 수도 없는
 * 모델이 한가운데 끼어든다. 사용자가 실제로 선택하는 순서대로 놓는다.
 */
const rankOf = (name: string) => {
  if (name === DEFAULT_MODEL) return 0;
  if (name === TRANSLATE_MODEL) return 2;
  return 1;
};

const orderModels = (list: OllamaModel[]) =>
  [...list].sort((a, b) => {
    const d = rankOf(a.name) - rankOf(b.name);
    return d !== 0 ? d : a.name.localeCompare(b.name);
  });

const LlmChat: React.FC = () => {
  const [activeHost, setActiveHost] = useState<string | null>(null);
  const [hostStates, setHostStates] = useState<Record<string, HostState>>({});
  const [models, setModels] = useState<OllamaModel[]>([]);
  const [loaded, setLoaded] = useState<LoadedModel[]>([]);
  const [err, setErr] = useState<string>('');
  const [checking, setChecking] = useState(true);
  const timer = useRef<number | null>(null);

  // 한글 → 영어 번역 도구
  const [ko, setKo] = useState('');
  const [en, setEn] = useState('');
  const [translating, setTranslating] = useState(false);
  const [transErr, setTransErr] = useState('');
  const [copied, setCopied] = useState(false);
  const enRef = useRef<HTMLTextAreaElement | null>(null);
  const koRef = useRef<HTMLTextAreaElement | null>(null);

  // 헤더 접기 — WebUI 를 넓게 보고 싶을 때. 선택을 브라우저에 기억한다.
  const [compact, setCompact] = useState(() => {
    try { return localStorage.getItem('fds_llm_compact') === '1'; } catch { return false; }
  });
  const toggleCompact = useCallback(() => {
    setCompact(v => {
      const nv = !v;
      try { localStorage.setItem('fds_llm_compact', nv ? '1' : '0'); } catch { /* 무시 */ }
      return nv;
    });
  }, []);

  // 한 호스트가 살아 있는지 확인 (Ollama API)
  const probe = useCallback(async (host: string): Promise<boolean> => {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 3000);
      const res = await fetch(`http://${host}:${OLLAMA_PORT}/api/version`, {
        signal: ctrl.signal,
      });
      clearTimeout(t);
      return res.ok;
    } catch {
      return false;
    }
  }, []);

  // 살아 있는 호스트를 순서대로 찾는다
  const resolveHost = useCallback(async () => {
    setChecking(true);
    setErr('');
    const states: Record<string, HostState> = {};
    let picked: string | null = null;

    for (const h of HOSTS) states[h.host] = 'checking';
    setHostStates({ ...states });

    for (const h of HOSTS) {
      // eslint-disable-next-line no-await-in-loop
      const alive = await probe(h.host);
      states[h.host] = alive ? 'ok' : 'dead';
      setHostStates({ ...states });
      if (alive && !picked) picked = h.host;
    }

    setActiveHost(picked);
    setChecking(false);
    if (!picked) setErr('두 주소 모두 응답하지 않습니다. 서버 또는 Ollama 상태를 확인하세요.');
    return picked;
  }, [probe]);

  // 모델 목록 + 현재 적재 상태
  const loadStatus = useCallback(async (host: string) => {
    try {
      const [tagRes, psRes] = await Promise.all([
        fetch(`http://${host}:${OLLAMA_PORT}/api/tags`),
        fetch(`http://${host}:${OLLAMA_PORT}/api/ps`),
      ]);
      if (tagRes.ok) {
        const d = await tagRes.json();
        setModels(orderModels(d.models ?? []));
      }
      if (psRes.ok) {
        const d = await psRes.json();
        setLoaded(d.models ?? []);
      }
      setErr('');
    } catch (e: any) {
      setErr(`상태 조회 실패: ${e?.message ?? e}`);
    }
  }, []);

  // 최초 1회 호스트 탐색
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const h = await resolveHost();
      if (!cancelled && h) await loadStatus(h);
    })();
    return () => { cancelled = true; };
  }, [resolveHost, loadStatus]);

  // 번역 결과가 채워지면 결과칸 높이를 내용에 맞춘다
  useEffect(() => { autoGrow(enRef.current); }, [en]);

  // 적재 상태 주기 갱신 (10초)
  useEffect(() => {
    if (!activeHost) return undefined;
    timer.current = window.setInterval(() => { loadStatus(activeHost); }, 10000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [activeHost, loadStatus]);

  /**
   * 한글 → 영어 번역.
   *
   * ★ 모델을 새로 띄우지 않는다. 동시 적재가 1개라서(MAX_LOADED_MODELS=1)
   *   전용 모델을 지정하면 번역할 때마다 스왑이 일어나 20~40초씩 두 번
   *   (번역용으로 갈아끼우고, 채팅으로 되돌리고) 낭비된다.
   *   그래서 **지금 올라와 있는 모델을 그대로 쓴다.** 아무것도 안 올라와
   *   있으면 기본 모델을 쓴다 — 어차피 한 번은 올려야 하기 때문이다.
   *
   * thinking 억제와 "번역만 하라"는 TRANSLATE_SYSTEM 주석 참조.
   */
  const translate = useCallback(async (dir: Dir) => {
    // ko2en 은 왼쪽(한글)을 읽어 오른쪽(영어)에, en2ko 는 그 반대
    const text = (dir === 'ko2en' ? ko : en).trim();
    if (!text || !activeHost) return;

    // 번역 전용 경량 모델을 쓴다. 설치돼 있지 않으면 적재된 모델로 폴백한다.
    const hasLight = models.some(m => m.name === TRANSLATE_MODEL);
    const model = hasLight ? TRANSLATE_MODEL : (loaded[0]?.name ?? DEFAULT_MODEL);

    setTranslating(true);
    setTransErr('');
    if (dir === 'ko2en') setEn(''); else setKo('');
    setCopied(false);

    // thinking 모델(qwen3 계열)일 때만 /no_think 를 붙인다.
    // 경량 instruct 모델은 이 지시어를 모르므로 그대로 본문에 섞일 수 있다.
    const isThinking = model.startsWith('qwen3');
    const sys = isThinking ? TRANSLATE_SYSTEM[dir] : TRANSLATE_SYSTEM[dir].replace(`${NO_THINK} `, '');
    const usr = isThinking ? `${NO_THINK} ${text}` : text;

    // 응답이 영영 안 오는 경우를 막는다 — 없으면 버튼이 '번역 중'에 멈춘다
    const ctrl = new AbortController();
    const killer = window.setTimeout(() => ctrl.abort(), 120000);

    try {
      const res = await fetch(`http://${activeHost}:${OLLAMA_PORT}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: ctrl.signal,
        body: JSON.stringify({
          model,
          messages: [
            { role: 'system', content: sys },
            { role: 'user', content: usr },
          ],
          stream: false,
          options: { temperature: 0 },
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status} — 모델 '${model}' 확인`);
      const d = await res.json();
      // thinking 모델이면 <think> 블록이 섞여 나올 수 있다
      const out = String(d?.message?.content ?? '')
        .replace(/<think>[\s\S]*?<\/think>/g, '')
        .trim();
      if (dir === 'ko2en') setEn(out); else setKo(out);
      if (!out) setTransErr(`번역 결과가 비었습니다 (모델 ${model}). 다시 시도해 주세요.`);
    } catch (e: any) {
      const msg = e?.name === 'AbortError'
        ? '번역 시간 초과(120초). 모델이 적재 중이거나 문장이 너무 깁니다.'
        : `번역 실패: ${e?.message ?? e}`;
      setTransErr(msg);
    } finally {
      window.clearTimeout(killer);
      setTranslating(false);
    }
  }, [ko, en, activeHost, loaded, models]);

  /**
   * 클립보드 복사.
   *
   * fdsapp 은 평문 HTTP(포트 80)로 서비스된다. `navigator.clipboard` 는
   * **보안 컨텍스트(HTTPS 또는 localhost)에서만** 존재하므로 여기서는
   * undefined 다 — 그래서 "복사 실패" 가 났다.
   *
   * 그래서 3단계로 시도한다:
   *   1) navigator.clipboard  (HTTPS 로 전환하면 이 경로가 쓰인다)
   *   2) execCommand('copy')  (구식이지만 HTTP 에서도 동작한다)
   *   3) 그래도 안 되면 텍스트를 선택해 두고 Ctrl+C 를 안내한다
   */
  const copyText = useCallback(async (text: string, el?: HTMLTextAreaElement | null) => {
    if (!text) return;

    // 1) 표준 API — 보안 컨텍스트에서만 존재
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setTransErr('');
        window.setTimeout(() => setCopied(false), 1500);
        return;
      } catch {
        /* 2) 로 내려간다 */
      }
    }

    // 2) execCommand 폴백 — HTTP 에서도 동작한다
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      // 화면 밖으로 빼되 focus 는 받을 수 있게 (display:none 이면 복사가 안 된다)
      ta.style.position = 'fixed';
      ta.style.top = '-1000px';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (ok) {
        setCopied(true);
        setTransErr('');
        window.setTimeout(() => setCopied(false), 1500);
        return;
      }
    } catch {
      /* 3) 으로 내려간다 */
    }

    // 3) 마지막 — 사용자가 바로 Ctrl+C 할 수 있게 선택해 준다
    if (el) {
      el.focus();
      el.select();
      setTransErr('자동 복사가 막혀 있습니다. 선택해 두었으니 Ctrl+C 를 누르세요.');
    } else {
      setTransErr('복사 실패 — 직접 선택해 복사해 주세요.');
    }
  }, []);

  const copyEn = useCallback(() => copyText(en, enRef.current), [copyText, en]);

  const webuiUrl = activeHost ? `http://${activeHost}:${WEBUI_PORT}` : '';

  const btn: React.CSSProperties = {
    padding: '4px 12px', background: '#3b82f6', color: 'white',
    border: 'none', borderRadius: '4px', fontSize: '12px', fontWeight: 500,
  };

  return (
    <div style={{
      padding: '10px', backgroundColor: '#eff6ff',
      // 부모 <main> 에 높이가 지정돼 있지 않아 height:100% 는 0 이 된다.
      // 상단 메뉴(2줄)와 main 의 패딩을 뺀 뷰포트 높이를 직접 준다.
      // 부모 <main> 에 높이가 없어 height:100% 는 0 이 된다. 뷰포트 기준으로 준다.
      //
      // 한때 200vh 로 키웠다가 되돌렸다 — iframe 이 화면 밖으로 넘치면
      // WebUI 의 로그인 버튼·입력창이 접근 불가가 된다(실측 확인).
      // 대신 헤더를 접어(compact) WebUI 에 최대한 넓은 자리를 준다.
      height: 'calc(100vh - 118px)', minHeight: 560,
      display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
    }}>

      {/* 상단 헤더 카드 — 서버 · 모델을 한 박스에 */}
      <div style={{
        backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px',
        padding: '10px 12px', marginBottom: '10px',
      }}>
        {/* 1행 — 서버 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, color: '#1e40af', fontSize: '14px', fontWeight: 600 }}>
            LLM
          </h3>

          {HOSTS.map(h => {
            const st = hostStates[h.host];
            const isActive = activeHost === h.host;
            const color = st === 'ok' ? '#16a34a' : st === 'dead' ? '#dc2626' : '#9ca3af';
            return (
              <div
                key={h.host}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '3px 10px', borderRadius: 4, fontSize: 12,
                  border: isActive ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                  background: isActive ? '#eff6ff' : '#f9fafb',
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? '#1e40af' : '#6b7280',
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: color, display: 'inline-block',
                }} />
                {h.label}
                {isActive && <span style={{ fontSize: 11 }}>사용중</span>}
              </div>
            );
          })}

          <button
            type="button"
            onClick={async () => { const h = await resolveHost(); if (h) await loadStatus(h); }}
            disabled={checking}
            style={{ ...btn, background: checking ? '#9ca3af' : '#3b82f6', cursor: checking ? 'wait' : 'pointer' }}
          >
            {checking ? '⏳ 확인 중...' : '🔄 재확인'}
          </button>

          <button
            type="button"
            onClick={toggleCompact}
            title={compact ? '번역칸·모델 목록 펼치기' : '접어서 아래 화면을 넓게 쓰기'}
            style={{
              ...btn, background: '#fff', color: '#1e40af',
              border: '1px solid #3b82f6', cursor: 'pointer',
            }}
          >
            {compact ? '▼ 펼치기' : '▲ 접기'}
          </button>

          {webuiUrl && (
            <a
              href={webuiUrl} target="_blank" rel="noreferrer"
              style={{ ...btn, marginLeft: 'auto', textDecoration: 'none', display: 'inline-block' }}
            >
              새 창으로 열기 ↗
            </a>
          )}
        </div>

        {err && (
          <div style={{ marginTop: 10, color: '#dc2626', fontSize: 12 }}>{err}</div>
        )}

        {/* 2행 — 모델 (같은 박스 안) */}
        {!compact && <div style={{
          marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb',
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 12, color: '#1e40af', fontWeight: 600 }}>
            모델
          </span>
          <span style={{ fontSize: 11, color: '#6b7280' }}>
            동시 적재 2개 (채팅 + 번역) · 첫 적재 20~40초
          </span>

          {models.length === 0 && (
            <span style={{ fontSize: 12, color: '#9ca3af' }}>
              {checking ? '조회 중...' : '모델 없음'}
            </span>
          )}

          {models.map(m => {
            const on = loaded.find(l => l.name === m.name);
            const hint = hintFor(m.name);
            const isDefault = m.name === DEFAULT_MODEL;
            // 번역 모델은 채팅에서 고를 수 없다 → 흐리게 해서 선택지와 구분한다
            const isTool = m.name === TRANSLATE_MODEL;
            return (
              <div
                key={m.name}
                title={`${fmtGB(m.size)}${m.details?.parameter_size ? ` · ${m.details.parameter_size}` : ''}${m.details?.quantization_level ? ` · ${m.details.quantization_level}` : ''}${isTool ? ' · 채팅에서 선택 불가' : ''}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  border: on ? '2px solid #16a34a' : '1px solid #e5e7eb',
                  background: on ? '#f0fdf4' : '#f9fafb',
                  borderRadius: 4, padding: '3px 10px', fontSize: 12,
                  opacity: isTool ? 0.65 : 1,
                  marginLeft: isTool ? 8 : 0,
                }}
              >
                {on && <span style={{ color: '#16a34a', fontSize: 11 }}>●</span>}
                <span style={{ fontWeight: 600, color: '#374151' }}>{m.name}</span>

                {isDefault && (
                  <span style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 3,
                    background: '#1e40af', color: '#fff', fontWeight: 600,
                  }}>
                    기본
                  </span>
                )}

                {hint && (
                  <span style={{
                    fontSize: 11, padding: '1px 7px', borderRadius: 10,
                    color: hint.color, border: `1px solid ${hint.color}`,
                    background: '#fff', whiteSpace: 'nowrap',
                  }}>
                    {hint.use}
                  </span>
                )}

                <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtGB(m.size)}</span>
              </div>
            );
          })}
        </div>}

        {/* 3행 — 한글→영어 번역 도구 */}
        {!compact && (
          <div style={{
            marginTop: 14, paddingTop: 12, borderTop: '1px solid #e5e7eb',
            display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap',
          }}>
            {/* 한글 입력 */}
            <div style={{ flex: '1 1 380px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              <label style={{ fontSize: 12, color: '#1e40af', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>한글 (Ctrl+Enter → 영어)</span>
                {ko && (
                  <button
                    type="button"
                    onClick={() => copyText(ko, koRef.current)}
                    style={{
                      fontSize: 10, padding: '1px 8px', borderRadius: 3,
                      border: '1px solid #3b82f6', background: '#fff',
                      color: '#1e40af', cursor: 'pointer', fontWeight: 600,
                    }}
                  >
                    복사
                  </button>
                )}
                <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>
                  번역: {models.some(m => m.name === TRANSLATE_MODEL) ? TRANSLATE_MODEL : (loaded[0]?.name ?? DEFAULT_MODEL)}
                </span>
              </label>
              <textarea
                value={ko}
                ref={el => { koRef.current = el; autoGrow(el); }}
                onInput={e => autoGrow(e.currentTarget)}
                onChange={e => { setKo(e.target.value); autoGrow(e.currentTarget); }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); translate('ko2en'); }
                }}
                placeholder="한글을 입력하고 [영어로 번역]"
                rows={4}
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'none', overflowY: 'auto',
                  padding: '8px 10px', fontSize: 13, lineHeight: 1.6, minHeight: 90,
                  border: '1px solid #d1d5db', borderRadius: 4, fontFamily: 'inherit',
                }}
              />
            </div>

            {/* 번역 버튼 — 양방향 */}
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
              <button
                type="button"
                onClick={() => translate('ko2en')}
                disabled={translating || !ko.trim() || !activeHost}
                title="왼쪽 한글을 오른쪽에 영어로"
                style={{
                  ...btn, whiteSpace: 'nowrap',
                  background: (translating || !ko.trim() || !activeHost) ? '#9ca3af' : '#3b82f6',
                  cursor: (translating || !ko.trim() || !activeHost) ? 'not-allowed' : 'pointer',
                }}
              >
                {translating ? '⏳ 번역 중...' : '영어로 번역 →'}
              </button>
              <button
                type="button"
                onClick={() => translate('en2ko')}
                disabled={translating || !en.trim() || !activeHost}
                title="오른쪽 영어를 왼쪽에 한글로"
                style={{
                  ...btn, whiteSpace: 'nowrap',
                  background: (translating || !en.trim() || !activeHost) ? '#9ca3af' : '#7c3aed',
                  cursor: (translating || !en.trim() || !activeHost) ? 'not-allowed' : 'pointer',
                }}
              >
                {translating ? '⏳ 번역 중...' : '← 한글로 번역'}
              </button>
              {(ko || en) && (
                <button
                  type="button"
                  onClick={() => { setKo(''); setEn(''); setTransErr(''); }}
                  style={{
                    ...btn, background: '#fff', color: '#6b7280',
                    border: '1px solid #d1d5db', cursor: 'pointer',
                  }}
                >
                  지우기
                </button>
              )}
            </div>

            {/* 영어 결과 */}
            <div style={{ flex: '1 1 380px', display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 12, color: '#1e40af', fontWeight: 600 }}>English (Ctrl+Enter → 한글)</label>
                {en && (
                  <button
                    type="button"
                    onClick={copyEn}
                    style={{
                      fontSize: 10, padding: '1px 8px', borderRadius: 3,
                      border: '1px solid #3b82f6',
                      background: copied ? '#16a34a' : '#fff',
                      color: copied ? '#fff' : '#1e40af',
                      cursor: 'pointer',
                    }}
                  >
                    {copied ? '✓ 복사됨' : '복사'}
                  </button>
                )}
              </div>
              <textarea
                value={en}
                ref={enRef}
                onInput={e => autoGrow(e.currentTarget)}
                onChange={e => { setEn(e.target.value); autoGrow(e.currentTarget); }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); translate('en2ko'); }
                }}
                rows={4}
                placeholder="영어를 붙여넣고 [← 한글로 번역] 하거나, 번역 결과가 여기 표시됩니다."
                style={{
                  width: '100%', boxSizing: 'border-box', resize: 'none', overflowY: 'auto',
                  padding: '8px 10px', fontSize: 13, lineHeight: 1.6, minHeight: 90,
                  border: '1px solid #d1d5db', borderRadius: 4, fontFamily: 'inherit',
                }}
              />
            </div>

            {transErr && (
              <div style={{ flexBasis: '100%', color: '#dc2626', fontSize: 12 }}>{transErr}</div>
            )}
          </div>
        )}
      </div>

      {/* Open WebUI */}
      <div style={{
        flex: 1, backgroundColor: 'white', border: '2px solid #3b82f6',
        borderRadius: '8px', overflow: 'hidden', minHeight: 0,
      }}>
        {webuiUrl ? (
          <iframe
            title="Open WebUI"
            src={webuiUrl}
            style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          />
        ) : (
          <div style={{ padding: 24, color: '#6b7280', fontSize: 13 }}>
            {checking
              ? '서버를 확인하는 중입니다...'
              : '접속 가능한 서버가 없습니다. 위 [재확인] 을 눌러 주세요.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default LlmChat;
